import React from "react";
import { Typography, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { EstadoInventarioBadge, StockInventario } from "../components/ui";
import { INVENTARIO_ESTADO, ESTADOS_ELIMINABLES, ESTADOS_FINALIZADOS } from "../constants";
import { resumenInventario } from "./transformers";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import DateUtils from "../../../utils/DateUtils";
import FechaLimiteInventario from "../components/ui/FechaLimiteInventario";

/**
 * Definiciones de columnas para el DataGrid de listas de inventario (admin y
 * "Mis inventarios" del preparador)
 */

/**
 * Genera las columnas del DataGrid de inventarios. Editar solo se ofrece en
 * PENDIENTE (reasignar uno en curso queda fuera de alcance); eliminar, en los
 * estados de ESTADOS_ELIMINABLES.
 * @param {Object} handlers - Callbacks de acciones de fila
 * @param {Function} handlers.onVer - Callback al pulsar ver detalle, recibe la fila completa
 * @param {Function} handlers.onEditar - Callback al pulsar editar, recibe la fila completa
 * @param {Function} handlers.onEliminar - Callback al pulsar eliminar, recibe la fila completa
 * @returns {Array} Definiciones de columnas para el DataGrid
 */
export const generateInventarioColumns = ({ onVer, onEditar, onEliminar }) => [
    {
        field: "id",
        headerName: "ID",
        flex: 0.4,
    },
    {
        field: "estado",
        headerName: "Estado",
        flex: 0.9,
        renderCell: (params) => <EstadoInventarioBadge estado={params.value} />,
    },
    {
        field: "preparadorNombre",
        headerName: "Preparador",
        flex: 1.2,
        renderCell: (params) => (
            <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: params.value ? "#495057" : "#6c757d" }}>
                {params.value || "Sin asignar"}
            </Typography>
        ),
    },
    {
        field: "lineas",
        headerName: "Artículos",
        flex: 0.7,
        valueGetter: (params) => params.value?.length ?? 0,
    },
    {
        field: "stock",
        headerName: "Stock por nave",
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: (params) => <StockInventario lineas={params.row.lineas} />,
    },
    {
        field: "fechaLimite",
        headerName: "Fecha límite",
        flex: 1,
        valueFormatter: (params) => (params.value ? DateUtils.formatearFecha(params.value) : "-"),
    },
    {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 0.9,
        cellClassName: "actions",
        // DECISION: los callbacks reciben la fila completa, no solo el id, misma
        // firma que rangos/helpers/columns.js, para reutilizar los mismos
        // handlers en InventariosMobileList (TASK-008) sin envoltorios
        getActions: ({ row }) => {
            const acciones = [
                <GridActionsCellItem
                    key="ver"
                    icon={<VisibilityIcon />}
                    label="Ver inventario"
                    onClick={() => onVer(row)}
                    color="inherit"
                />,
            ];

            if (row.estado === INVENTARIO_ESTADO.PENDIENTE) {
                acciones.push(
                    <GridActionsCellItem
                        key="editar"
                        icon={<EditIcon />}
                        label="Editar inventario"
                        onClick={() => onEditar(row)}
                        color="inherit"
                    />
                );
            }

            if (ESTADOS_ELIMINABLES.includes(row.estado)) {
                acciones.push(
                    <GridActionsCellItem
                        key="eliminar"
                        icon={<DeleteIcon />}
                        label="Eliminar inventario"
                        onClick={() => onEliminar(row)}
                        color="inherit"
                    />
                );
            }

            return acciones;
        },
    },
];

/**
 * Genera las columnas del DataGrid de "Mis inventarios" (TASK-012): el
 * fallback de escritorio, ya que la vista PRINCIPAL de esa pantalla son las
 * tarjetas de MisInventariosMobileList (ver pages/MisInventarios.js). La acción
 * va en la columna final, como en el resto de features: "Contar" si aún se puede
 * contar y "Ver resultado" si ya está finalizado; onVer recibe la fila completa.
 * @param {{ onVer: Function }} handlers - Abre el conteo o el resultado del inventario
 * @returns {Array} Definiciones de columnas para el DataGrid de "Mis inventarios"
 */
export const generateMisInventariosColumns = ({ onVer }) => [
    {
        field: "estado",
        headerName: "Estado",
        flex: 0.9,
        renderCell: (params) => <EstadoInventarioBadge estado={params.value} />,
    },
    {
        field: "progreso",
        headerName: "Progreso",
        flex: 0.9,
        // Se ordena por proporción contada (valueGetter) y se pinta "2 de 5"; el
        // recuento sale de resumenInventario para no duplicar qué cuenta como contado
        valueGetter: (params) => {
            const { contadas, totalLineas } = resumenInventario(params.row);
            return totalLineas ? contadas / totalLineas : 0;
        },
        renderCell: (params) => {
            const { contadas, totalLineas } = resumenInventario(params.row);
            return `${contadas} de ${totalLineas}`;
        },
    },
    {
        field: "stock",
        headerName: "Stock por nave",
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: (params) => <StockInventario lineas={params.row.lineas} />,
    },
    {
        field: "fechaLimite",
        headerName: "Fecha límite",
        flex: 1.2,
        renderCell: (params) => <FechaLimiteInventario inventario={params.row} />,
    },
    {
        field: "observaciones",
        headerName: "Observaciones",
        flex: 1.6,
        sortable: false,
        // Recortadas a una línea; el texto completo, al pasar el ratón
        renderCell: (params) =>
            params.value ? (
                <Tooltip title={params.value}>
                    <Typography noWrap sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: "#495057" }}>
                        {params.value}
                    </Typography>
                </Tooltip>
            ) : (
                <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: "#6c757d" }}>-</Typography>
            ),
    },
    {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 0.5,
        cellClassName: "actions",
        getActions: ({ row }) => {
            const finalizado = ESTADOS_FINALIZADOS.includes(row.estado);
            return [
                <GridActionsCellItem
                    key="ver"
                    icon={finalizado ? <VisibilityIcon /> : <EditNoteIcon />}
                    label={finalizado ? "Ver resultado" : "Contar inventario"}
                    onClick={() => onVer(row)}
                    color="inherit"
                />,
            ];
        },
    },
];
